/**
 * Controls how many existing task rows are visible in a compact queue.
 * There is one queue and one backend source of truth; this is presentation only.
 */
export const TASK_QUEUE_PAGE_SIZE = 5;

export function visibleTaskRows(items, compact, count = TASK_QUEUE_PAGE_SIZE) {
  if (!Array.isArray(items)) return [];
  if (!compact) return items;
  return items.slice(0, Math.max(TASK_QUEUE_PAGE_SIZE, count));
}

export function remainingTaskRows(items, compact, count = TASK_QUEUE_PAGE_SIZE) {
  if (!compact || !Array.isArray(items)) return 0;
  return Math.max(0, items.length - visibleTaskRows(items, true, count).length);
}

export function newestVerificationFirst(items) {
  if (!Array.isArray(items)) return [];
  const timestamp = (row) => {
    const raw = row?.uploaded_at;
    const time = raw ? new Date(raw).getTime() : Number.NaN;
    return Number.isFinite(time) ? time : null;
  };
  return [...items].sort((a, b) => {
    const left = timestamp(a);
    const right = timestamp(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return right - left;
  });
}

/**
 * A saved but pending document needs an evidence review first, not a second
 * applicant notification. The queue may offer a direct replacement request
 * ONLY for a fileless, unprogressed row that the backend explicitly classifies
 * as a replacement task; historical uploads and known-unavailable links need
 * a manager's provenance review. The API is independently permission-gated.
 */
export function needsEvidenceHistoryReview(task) {
  return Boolean(
    task?.recovery_review_required ||
    task?.evidence_issue === 'known_storage_unavailable' ||
    ['uploaded', 'approved', 'verified', 'complete', 'completed', 'accepted', 'active']
      .includes(String(task?.document_status || '').toLowerCase())
  );
}

export function canRequestReplacementFromQueue(task) {
  return Boolean(
    task?.type === 'replacement' &&
    task?.action === 'request_replacement' &&
    task?.evidence_issue === 'no_file_reference' &&
    (task.document_id || task.id) &&
    !needsEvidenceHistoryReview(task)
  );
}

/**
 * A missing/partial API contract cannot be silently displayed as
 * "all tasks complete". This is a trust boundary, not a rendering nicety.
 */
export function isCompleteTaskQueuePayload(payload) {
  return Boolean(
    payload && Array.isArray(payload.tasks) &&
    Number.isInteger(payload.total_tasks) && payload.total_tasks >= 0 &&
    payload.total_tasks === payload.tasks.length &&
    typeof payload.partial_results === 'boolean' &&
    Array.isArray(payload.source_limits_reached)
  );
}
